/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Blog page type — header + footer layout with PD top zone (Hero 1/Content 1) and 2-col article tile grid
import type { ReactElement } from 'react';
import type { Route } from './+types/_app.baggallini-blog[.html]';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Region } from '@/components/region';
import { fetchPageWithComponentData, type PageWithComponentData } from '@/lib/page-designer/page-loader.server';

@PageType({
    name: 'Blog',
    description: 'Blog landing page with a top hero/editorial zone and a 2-per-row article tile grid.',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'top',
        name: 'Top',
        description: 'Hero or editorial block above the article grid. Accepts Hero 1 or Content 1.',
        maxComponents: 2,
    },
    {
        id: 'main',
        name: 'Articles',
        description: 'Article tiles — stack 2 per row. Add up to 20 Article Tile components.',
        maxComponents: 20,
    },
])
export class BlogPageMetadata {}

export type BlogPageData = {
    page: PageWithComponentData | null;
};

export async function loader(args: Route.LoaderArgs): Promise<BlogPageData> {
    return {
        page: await fetchPageWithComponentData(args, { pageId: 'baggallini-blog-storefrontnext' }),
    };
}

// RGBARRY-CUSTOMIZATION: classify this route for the GTM data layer's page-view event
// (see src/analytics/route-analytics.ts).
export const handle = {
    analytics: { routeType: 'content' },
};

export default function BlogPage({ loaderData }: { loaderData: BlogPageData }): ReactElement {
    const { page } = loaderData;

    return (
        <div>
            {/* Top zone — Hero 1 or Content 1 */}
            <Region page={page} regionId="top" errorElement={null} fallbackElement={null} />

            {/* Article grid — 2 columns on large screens */}
            <div className="section-container px-6 lg:px-8 py-12 lg:py-16">
                <Region
                    page={page}
                    regionId="main"
                    className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12"
                    errorElement={null}
                    fallbackElement={null}
                />
            </div>
        </div>
    );
}
